import {
  type MouseEvent, useCallback, useMemo, useRef, useState,
} from 'react';
import { RAIL_CLASS } from './slider.constants';

type UseRailHoveredHandleParams = Readonly<{
  /** Handle indexes eligible for highlighting, with their current values. */
  candidates: ReadonlyArray<Readonly<{ index: number; value: number }>>;
  min: number;
  max: number;
  enabled?: boolean;
  vertical?: boolean;
  reverse?: boolean;
}>;

const getPointerValue = (
  rail: Element,
  { clientX, clientY }: MouseEvent,
  { min, max, vertical, reverse }: Pick<UseRailHoveredHandleParams, 'min' | 'max' | 'vertical' | 'reverse'>,
): number => {
  const rect = rail.getBoundingClientRect();
  const rawFraction = vertical
    ? (rect.bottom - clientY) / rect.height
    : (clientX - rect.left) / rect.width;
  const fraction = Math.min(Math.max(reverse ? 1 - rawFraction : rawFraction, 0), 1);
  return min + fraction * (max - min);
};

/** Mirrors rc-slider's click logic: the nearest handle, and the later one on ties. */
const findNearestHandleIndex = (
  candidates: UseRailHoveredHandleParams['candidates'],
  pointerValue: number,
): number | null =>
  candidates.reduce<{ index: number; distance: number } | null>((nearest, { index, value }) => {
    const distance = Math.abs(pointerValue - value);
    return !nearest || distance <= nearest.distance ? { index, distance } : nearest;
  }, null)?.index ?? null;

const sliderClassNames = { rail: RAIL_CLASS };

export const useRailHoveredHandle = ({
  candidates, min, max, enabled = true, vertical, reverse,
}: UseRailHoveredHandleParams) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const railRef = useRef<Element | null>(null);

  const onMouseMove = useCallback((event: MouseEvent<HTMLElement>) => {
    // The rail can remount (e.g. single <-> range slider), so re-query once the cached one is detached
    if (!railRef.current?.isConnected) {
      railRef.current = event.currentTarget.querySelector(`.${RAIL_CLASS}`);
    }
    const rail = railRef.current;
    // A pressed button while moving means the user is dragging a handle
    const isDragging = event.buttons !== 0;
    setHoveredIndex(enabled && rail && !isDragging
      ? findNearestHandleIndex(candidates, getPointerValue(rail, event, { min, max, vertical, reverse }))
      : null);
  }, [candidates, enabled, min, max, vertical, reverse]);

  const onMouseLeave = useCallback(() => setHoveredIndex(null), []);

  return useMemo(() => ({
    hoveredIndex,
    onMouseMove,
    onMouseLeave,
    classNames: sliderClassNames,
  }), [hoveredIndex, onMouseMove, onMouseLeave]);
};
